import { PRODUCT_FOLDERS } from '@/config/folders'
import { makePdfDataUrl, uniqueName } from '@/lib/files'
import type { WorkspaceRepository } from './repository'
import { WORKSPACE_LOCAL_STORAGE_KEY } from './storageKey'
import type { WorkspaceNode } from './types'

const STORAGE_KEY = WORKSPACE_LOCAL_STORAGE_KEY
const delay = (ms = 40) => new Promise((resolve) => setTimeout(resolve, ms))

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function newId(): string {
  return crypto.randomUUID()
}

function storeKey(code: string, slug: string): string {
  return `${code.toUpperCase()}::${slug}`
}

function seedNode(
  productCode: string,
  folderSlug: string,
  name: string,
  kind: WorkspaceNode['kind'],
  extra: Partial<WorkspaceNode> = {},
): WorkspaceNode {
  return {
    id: newId(),
    productCode,
    folderSlug,
    parentId: null,
    name,
    kind,
    updatedAt: extra.updatedAt ?? today(),
    ...extra,
  }
}

const store = new Map<string, WorkspaceNode[]>()

function persistStore(): void {
  try {
    const payload: Record<string, WorkspaceNode[]> = {}
    for (const [key, nodes] of store.entries()) {
      payload[key] = nodes
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (err) {
    console.warn('Không lưu được workspace (dung lượng localStorage?):', err)
  }
}

function hydrateStore(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const payload = JSON.parse(raw) as Record<string, WorkspaceNode[]>
    store.clear()
    for (const [key, nodes] of Object.entries(payload)) {
      if (Array.isArray(nodes)) store.set(key, nodes)
    }
    return store.size > 0
  } catch {
    return false
  }
}

function ensureStore(code: string, slug: string): WorkspaceNode[] {
  const key = storeKey(code, slug)
  let nodes = store.get(key)
  if (!nodes) {
    nodes = []
    store.set(key, nodes)
  }
  return nodes
}

function ensureProductWorkspaces(code: string): void {
  for (const folder of PRODUCT_FOLDERS) {
    ensureStore(code, folder.slug)
  }
}

const SEED_CODES = [
  'MP123',
  'MP175',
  'MP201',
  'MP210',
  'MP220',
  'MP301',
  'MP305',
  'MP410',
]

function seedDefaults(): void {
  for (const code of SEED_CODES) ensureProductWorkspaces(code)

  const drawing = ensureStore('MP175', 'drawing')
  drawing.push(
    seedNode('MP175', 'drawing', 'MP175_Overall.pdf', 'file', {
      mimeType: 'application/pdf',
      updatedAt: '2026-08-12',
      dataUrl: makePdfDataUrl('MP175 Overall Drawing'),
    }),
    seedNode('MP175', 'drawing', 'MP175_Detail.dwg', 'file', {
      mimeType: 'application/acad',
      updatedAt: '2026-08-10',
    }),
    seedNode('MP175', 'drawing', 'MP175_Dimension.pdf', 'file', {
      mimeType: 'application/pdf',
      updatedAt: '2026-08-09',
      dataUrl: makePdfDataUrl('MP175 Dimension Drawing'),
    }),
  )
  ensureStore('MP175', 'bom').push(
    seedNode('MP175', 'bom', 'MP175_BOM.xlsx', 'file', {
      mimeType:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      updatedAt: '2026-08-11',
    }),
  )
  ensureStore('MP175', 'picture').push(
    seedNode('MP175', 'picture', 'MP175_Render.jpg', 'file', {
      mimeType: 'image/jpeg',
      updatedAt: '2026-07-20',
      dataUrl:
        'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=1200&h=900&fit=crop',
    }),
    seedNode('MP175', 'picture', 'MP175_Front.jpg', 'file', {
      mimeType: 'image/jpeg',
      updatedAt: '2026-07-18',
      dataUrl:
        'https://images.unsplash.com/photo-1538688525198-9b88f6f53140?w=1200&h=900&fit=crop',
    }),
    seedNode('MP175', 'picture', 'MP175_Side.jpg', 'file', {
      mimeType: 'image/jpeg',
      updatedAt: '2026-07-18',
      dataUrl:
        'https://images.unsplash.com/photo-1611269154421-4e27233ac5c7?w=1200&h=900&fit=crop',
    }),
  )
  ensureStore('MP201', 'drawing').push(
    seedNode('MP201', 'drawing', 'MP201_Overall.pdf', 'file', {
      mimeType: 'application/pdf',
      updatedAt: '2026-08-01',
      dataUrl: makePdfDataUrl('MP201 Overall Drawing'),
    }),
  )
  ensureStore('MP201', 'picture').push(
    seedNode('MP201', 'picture', 'MP201_Front.jpg', 'file', {
      mimeType: 'image/jpeg',
      updatedAt: '2026-07-15',
      dataUrl:
        'https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=1200&h=900&fit=crop',
    }),
    seedNode('MP201', 'picture', 'MP201_Open.jpg', 'file', {
      mimeType: 'image/jpeg',
      updatedAt: '2026-07-15',
      dataUrl:
        'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1200&h=900&fit=crop',
    }),
  )
}

;(function initWorkspace() {
  const restored = typeof localStorage !== 'undefined' && hydrateStore()
  if (!restored) {
    seedDefaults()
    persistStore()
  } else {
    for (const code of SEED_CODES) ensureProductWorkspaces(code)
    // Ensure newly added folders (e.g. report) exist for every known product key
    for (const key of [...store.keys()]) {
      const code = key.split('::')[0]
      if (code) ensureProductWorkspaces(code)
    }
    persistStore()
  }
})()

function getNodes(code: string, slug: string): WorkspaceNode[] {
  ensureProductWorkspaces(code)
  return ensureStore(code, slug)
}

function setNodes(code: string, slug: string, nodes: WorkspaceNode[]): void {
  store.set(storeKey(code, slug), nodes)
  persistStore()
}

function descendantsOf(nodes: WorkspaceNode[], id: string): WorkspaceNode[] {
  const result: WorkspaceNode[] = []
  const walk = (parentId: string) => {
    for (const node of nodes) {
      if (node.parentId === parentId) {
        result.push(node)
        if (node.kind === 'folder') walk(node.id)
      }
    }
  }
  walk(id)
  return result
}

function wouldNestInside(nodes: WorkspaceNode[], movingIds: string[], targetParentId: string | null): boolean {
  if (!targetParentId) return false
  if (movingIds.includes(targetParentId)) return true
  for (const id of movingIds) {
    const nested = descendantsOf(nodes, id)
    if (nested.some((n) => n.id === targetParentId)) return true
  }
  return false
}

function siblingNames(nodes: WorkspaceNode[], parentId: string | null, exceptId?: string): string[] {
  return nodes
    .filter((n) => n.parentId === parentId && n.id !== exceptId)
    .map((n) => n.name)
}

function cloneSubtree(
  source: WorkspaceNode[],
  ids: string[],
  targetCode: string,
  targetSlug: string,
  targetParentId: string | null,
  target: WorkspaceNode[],
): WorkspaceNode[] {
  const created: WorkspaceNode[] = []
  const clone = (node: WorkspaceNode, parentId: string | null) => {
    const name = uniqueName(siblingNames([...target, ...created], parentId), node.name)
    const copy: WorkspaceNode = {
      ...node,
      id: newId(),
      productCode: targetCode,
      folderSlug: targetSlug,
      parentId,
      name,
      updatedAt: today(),
    }
    created.push(copy)
    if (node.kind === 'folder') {
      for (const child of source.filter((n) => n.parentId === node.id)) {
        clone(child, copy.id)
      }
    }
  }
  for (const id of ids) {
    const node = source.find((n) => n.id === id)
    if (node) clone(node, targetParentId)
  }
  return created
}

const MOCK_MAX_TOTAL = 5 * 1024 * 1024 * 1024
const MOCK_MAX_FILE = 50 * 1024 * 1024

export const mockWorkspaceRepository: WorkspaceRepository = {
  async list(productCode, folderSlug, parentId) {
    await delay()
    const nodes = getNodes(productCode, folderSlug)
    return nodes
      .filter((n) => n.parentId === parentId)
      .sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1
        return a.name.localeCompare(b.name, 'vi')
      })
  },

  async getQuota() {
    await delay()
    let usedBytes = 0
    for (const nodes of store.values()) {
      for (const n of nodes) {
        if (n.kind === 'file' && typeof n.size === 'number') usedBytes += n.size
      }
    }
    return {
      usedBytes,
      maxTotalBytes: MOCK_MAX_TOTAL,
      maxFileBytes: MOCK_MAX_FILE,
      warn: usedBytes >= MOCK_MAX_TOTAL * 0.9,
    }
  },

  async getPath(productCode, folderSlug, nodeId) {
    await delay()
    if (!nodeId) return []
    const nodes = getNodes(productCode, folderSlug)
    const path: WorkspaceNode[] = []
    let current = nodes.find((n) => n.id === nodeId) ?? null
    const guard = new Set<string>()
    while (current && !guard.has(current.id)) {
      guard.add(current.id)
      path.unshift(current)
      current = current.parentId
        ? (nodes.find((n) => n.id === current!.parentId) ?? null)
        : null
    }
    return path
  },

  async createFolder(productCode, folderSlug, parentId, name = 'Thư mục mới') {
    await delay()
    const nodes = getNodes(productCode, folderSlug)
    const folder: WorkspaceNode = {
      id: newId(),
      productCode,
      folderSlug,
      parentId,
      name: uniqueName(siblingNames(nodes, parentId), name),
      kind: 'folder',
      updatedAt: today(),
    }
    setNodes(productCode, folderSlug, [...nodes, folder])
    return folder
  },

  async uploadFiles(productCode, folderSlug, parentId, files) {
    await delay()
    const nodes = getNodes(productCode, folderSlug)
    const created: WorkspaceNode[] = []
    const names = siblingNames(nodes, parentId)
    for (const file of files) {
      const name = uniqueName([...names, ...created.map((c) => c.name)], file.name)
      let dataUrl = file.dataUrl
      if (!dataUrl && file.file) {
        dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(String(reader.result))
          reader.onerror = () =>
            reject(reader.error ?? new Error('Không đọc được file.'))
          reader.readAsDataURL(file.file!)
        })
      }
      created.push({
        id: newId(),
        productCode,
        folderSlug,
        parentId,
        name,
        kind: 'file',
        mimeType: file.mimeType,
        size: file.size ?? (file.file ? file.file.size : undefined),
        dataUrl,
        updatedAt: today(),
      })
    }
    setNodes(productCode, folderSlug, [...nodes, ...created])
    return created
  },

  async rename(productCode, folderSlug, id, name) {
    await delay()
    const nodes = getNodes(productCode, folderSlug)
    const index = nodes.findIndex((n) => n.id === id)
    if (index < 0) throw new Error('Không tìm thấy mục.')
    const nextName = uniqueName(
      siblingNames(nodes, nodes[index].parentId, id),
      name.trim() || nodes[index].name,
    )
    const updated = { ...nodes[index], name: nextName, updatedAt: today() }
    const next = [...nodes]
    next[index] = updated
    setNodes(productCode, folderSlug, next)
    return updated
  },

  async remove(productCode, folderSlug, ids) {
    await delay()
    const nodes = getNodes(productCode, folderSlug)
    const removeIds = new Set(ids)
    for (const id of ids) {
      for (const child of descendantsOf(nodes, id)) removeIds.add(child.id)
    }
    setNodes(
      productCode,
      folderSlug,
      nodes.filter((n) => !removeIds.has(n.id)),
    )
  },

  async move(sourceCode, sourceSlug, ids, targetCode, targetSlug, targetParentId) {
    await delay()
    const same =
      sourceCode.toUpperCase() === targetCode.toUpperCase() &&
      sourceSlug === targetSlug
    if (same) {
      const nodes = getNodes(sourceCode, sourceSlug)
      if (wouldNestInside(nodes, ids, targetParentId)) {
        throw new Error('Không thể di chuyển thư mục vào chính nó.')
      }
      const next = nodes.map((node) => {
        if (!ids.includes(node.id)) return node
        return {
          ...node,
          parentId: targetParentId,
          name: uniqueName(siblingNames(nodes, targetParentId, node.id), node.name),
          updatedAt: today(),
        }
      })
      setNodes(sourceCode, sourceSlug, next)
      return
    }

    const source = getNodes(sourceCode, sourceSlug)
    const target = getNodes(targetCode, targetSlug)
    const cloned = cloneSubtree(source, ids, targetCode, targetSlug, targetParentId, target)
    setNodes(targetCode, targetSlug, [...target, ...cloned])
    const removeIds = new Set(ids)
    for (const id of ids) {
      for (const child of descendantsOf(source, id)) removeIds.add(child.id)
    }
    setNodes(
      sourceCode,
      sourceSlug,
      source.filter((n) => !removeIds.has(n.id)),
    )
  },

  async copy(sourceCode, sourceSlug, ids, targetCode, targetSlug, targetParentId) {
    await delay()
    const source = getNodes(sourceCode, sourceSlug)
    const target = getNodes(targetCode, targetSlug)
    if (
      sourceCode.toUpperCase() === targetCode.toUpperCase() &&
      sourceSlug === targetSlug &&
      wouldNestInside(source, ids, targetParentId)
    ) {
      throw new Error('Không thể sao chép thư mục vào chính nó.')
    }
    const cloned = cloneSubtree(source, ids, targetCode, targetSlug, targetParentId, target)
    setNodes(targetCode, targetSlug, [...target, ...cloned])
    return cloned
  },
}

export function ensureWorkspaceForProduct(code: string): void {
  ensureProductWorkspaces(code)
  persistStore()
}

/** Move all workspace folders from old item_code to new item_code. */
export function migrateWorkspaceProductCode(oldCode: string, newCode: string): void {
  const from = oldCode.toUpperCase()
  const to = newCode.toUpperCase()
  if (from === to) return
  ensureProductWorkspaces(newCode)
  for (const folder of PRODUCT_FOLDERS) {
    const oldKey = storeKey(from, folder.slug)
    const newKey = storeKey(to, folder.slug)
    const nodes = store.get(oldKey) ?? []
    const migrated = nodes.map((node) => ({
      ...node,
      productCode: to,
    }))
    const existing = store.get(newKey) ?? []
    store.set(newKey, [...existing, ...migrated])
    store.delete(oldKey)
  }
  persistStore()
}
